import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge } from '../../../../components/ui';
import { getThemeColors } from '../../../../lib/theme';
import { Rating } from '../../profile/controls';
import i18n from '../../../../i18n';
import { formatDate } from '../../../../lib/dateFormat';
import {
  STATUS_TONE,
  statusLabel,
  parseDate,
  type CareRow,
} from '../../CareYearTimeline';
import { cellStyle } from './constants';
import type { V1User } from '../../../../lib/v1';
import { buildPath } from '../../../../lib/route';

/**
 * Profil-Link des Partners einer Betreuung: im PB-Profil die BK
 * (/betreuung/{id}), im BK-Profil der PB (/pflege/{users.id}).
 */
export function carePartnerHref(partnerId: number | null | undefined, partnerIsBk: boolean): string | null {
  if (!partnerId) return null;
  return buildPath(partnerIsBk ? 'betreuung' : 'pflege', String(partnerId));
}

/** Name des Partners als Link, der das Profil in einem neuen Tab öffnet. */
export function CarePartnerLink({ name, href, color, fontSize }: { name: string | null | undefined; href: string | null; color: string; fontSize?: number }) {
  const { t: tr } = useTranslation();
  if (!href || !name) return <>{name ?? '—'}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="care-partner-link"
      title={tr('pflege.cares.open_profile_new_tab')}
      // Die Zeile selbst öffnet den Bearbeiten-Drawer — der Link darf das nicht auslösen.
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      style={{ color, fontSize, textDecoration: 'underline', textUnderlineOffset: 2 }}
    >{name}</a>
  );
}

export function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—';
  const d = parseDate(v);
  if (!d) return String(v);
  // Einheitlich „26.10.2026“ — unabhängig von der UI-Sprache.
  return formatDate(d);
}

export function CareTableRow({ care: c, user, dark, onEdit }: { care: CareRow; user: V1User | null; dark: boolean; onEdit?: () => void }) {
  const { t: tr } = useTranslation();
  const tc = getThemeColors(dark);
  const partnerName = user?.user_role === 2 ? c.bk_name : c.pb_name;
  const partnerId   = user?.user_role === 2 ? c.bk_user_id : c.pb_user_id;
  const partnerHref = carePartnerHref(partnerId, user?.user_role === 2);
  const rating      = user?.user_role === 2 ? (c as CareRow & { pb_rating?: number | null }).pb_rating
                                            : (c as CareRow & { bk_rating?: number | null }).bk_rating;
  const stTone  = STATUS_TONE[c.status] ?? 'neutral';
  const stLabel = statusLabel(c.status, (k, opts) => i18n.t(k, opts));
  return (
    <tr
      style={{ borderTop: `1px solid ${tc.borderSoft}`, background: c.status === 1 ? tc.cardMuted : undefined, cursor: onEdit ? 'pointer' : undefined }}
      onClick={onEdit}
      tabIndex={onEdit ? 0 : undefined}
      role={onEdit ? 'button' : undefined}
      aria-label={onEdit ? tr('pflege.cares.edit_aria', { id: c.id }) : undefined}
      onKeyDown={onEdit ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } } : undefined}
    >
      <td style={cellStyle}><Badge dark={dark} tone={stTone}>{stLabel}</Badge></td>
      <td style={cellStyle}><strong style={{ color: tc.heading }}>{fmtDate(c.startdatum)}</strong></td>
      <td style={cellStyle}>{fmtDate(c.enddatum)}</td>
      <td style={cellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={partnerName ?? '?'} userId={partnerId ?? null} size={32} tone={user?.user_role === 2 ? 'green' : 'blue'} />
          <div>
            <div style={{ fontWeight: 650, color: tc.heading }}><CarePartnerLink name={partnerName} href={partnerHref} color={tc.heading} /></div>
            {partnerId ? <div style={{ fontSize: 11, color: tc.muted }}>#{partnerId}</div> : null}
          </div>
        </div>
      </td>
      <td style={cellStyle}><MatchingScore value={(c as CareRow & { matching?: number | null }).matching ?? null} dark={dark} /></td>
      <td style={cellStyle}>{rating ? <Rating dark={dark} value={Number(rating)} /> : '—'}</td>
    </tr>
  );
}

export function Th({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const tc = getThemeColors(dark);
  return (
    <th style={{
      padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 800, color: tc.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${tc.border}`,
    }}>{children}</th>
  );
}

export function MatchingScore({ value, dark }: { value: number | null; dark: boolean }) {
  const tc = getThemeColors(dark);
  if (value == null) return <span style={{ color: tc.muted }}>—</span>;
  const score = Math.round(value);
  const tone = score >= 80 ? '#027a48' : score >= 60 ? '#b54708' : '#b42318';
  const bg   = score >= 80 ? (dark ? '#0f3a23' : '#ecfdf3')
             : score >= 60 ? (dark ? '#3a2a0f' : '#fffaeb')
             :               (dark ? '#3a0f0c' : '#fef3f2');
  return (
    <span style={{ background: bg, color: tone, fontWeight: 800, fontSize: 12, padding: '4px 9px', borderRadius: 999, display: 'inline-block', minWidth: 42, textAlign: 'center' }}>
      {score}%
    </span>
  );
}
